import ArrowOutward from '@mui/icons-material/ArrowOutward';
import MailOutline from '@mui/icons-material/MailOutline';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { AppLink } from '@/components/atoms';
import { ContactCard, footerLinkSx, PhoneChannelLink } from '@/components/molecules';
import { type FooterContent } from '@/hooks/useFooterContent';

type Contact = FooterContent['contact'];

/**
 * Kabex has an office, not a showroom: there is no floor of cars to walk and
 * nothing on display, so the card invites the visitor to the team rather than
 * to a stock list. The address, the directions link and the map on /contact
 * are all the same place and must agree on what it is called.
 */
function OfficeCard({ contact }: { contact: Contact }) {
  return (
    <ContactCard icon="location" title="Visit our office">
      <Typography component="address" variant="body2" sx={{ fontStyle: 'normal' }}>
        {contact.addressLines.map((line) => (
          <Box key={line} component="span" sx={{ display: 'block' }}>
            {line}
          </Box>
        ))}
        <Box component="span" sx={{ display: 'block', color: 'text.secondary' }}>
          {contact.postalLine}
        </Box>
      </Typography>
      <AppLink
        href={contact.directionsUrl}
        variant="body2"
        underline="hover"
        sx={{ mt: 3, display: 'inline-flex', alignItems: 'center', gap: 1, fontWeight: 700 }}
      >
        Get directions
        <ArrowOutward aria-hidden sx={{ fontSize: 16 }} />
      </AppLink>
    </ContactCard>
  );
}

function TalkCard({ contact }: { contact: Contact }) {
  return (
    <ContactCard icon="phone" title="Talk to us">
      <Stack component="address" spacing={1.5} sx={{ fontStyle: 'normal' }}>
        {/* Not a `tel:` link each: the Japan line is a WhatsApp desk and says
            so, which is `PhoneChannelLink`'s whole job. The two Uganda lines
            render exactly as they did. */}
        {contact.phones.map((p) => (
          <PhoneChannelLink key={p.id} phone={p} variant="body2" sx={footerLinkSx}>
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
              {p.display}
            </Box>{' '}
            · {p.country}
          </PhoneChannelLink>
        ))}
        {[contact.inquiryEmail, contact.email].map((email) => (
          <AppLink
            key={email}
            href={`mailto:${email}`}
            variant="body2"
            sx={{ ...footerLinkSx, display: 'inline-flex', alignItems: 'center', gap: 1.5 }}
          >
            <MailOutline aria-hidden sx={{ fontSize: 16 }} />
            {email}
          </AppLink>
        ))}
      </Stack>
    </ContactCard>
  );
}

function HoursCard({ contact }: { contact: Contact }) {
  return (
    <ContactCard icon="clock" title="Opening hours">
      <Box component="dl" sx={{ m: 0, typography: 'body2' }}>
        {contact.hours.map((h) => (
          <Stack
            key={h.label}
            direction="row"
            justifyContent="space-between"
            spacing={3}
            sx={{ py: 1.5, borderBottom: 1, borderColor: 'divider' }}
          >
            <Box component="dt" sx={{ color: 'text.secondary' }}>
              {h.label}
            </Box>
            <Box component="dd" sx={{ m: 0, fontWeight: 600, textAlign: 'right' }}>
              {h.range}
            </Box>
          </Stack>
        ))}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        {contact.closedNote}
      </Typography>
    </ContactCard>
  );
}

const cards = [
  { id: 'office', Card: OfficeCard },
  { id: 'talk', Card: TalkCard },
  { id: 'hours', Card: HoursCard },
];

/** Office, phone and hours cards, side by side from md up. */
export function FooterContactStrip({ contact }: { contact: Contact }) {
  return (
    <Grid container spacing={4}>
      {cards.map(({ id, Card }) => (
        <Grid key={id} size={{ xs: 12, md: 4 }}>
          <Card contact={contact} />
        </Grid>
      ))}
    </Grid>
  );
}
